import React from 'react'
import { motion } from 'framer-motion'
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX, 
  Music, 
  Video,
  Maximize,
  Minimize2,
  RotateCcw,
  Shield,
  AlertTriangle
} from 'lucide-react'
import { gameUIVariants } from '@/lib/animations'
import { useMediaPlayer } from '@/hooks/useMediaPlayer'
import { useDRMSession } from '@/hooks/useDRMSession'
import { MediaPlayerProps } from '@/types/media'
import { getMediaType, formatDuration } from '@/lib/media-utils'
import { DRMPlayer } from '@/components/drm/DRMPlayer'
import { AntiPiracyDetector } from '@/components/drm/AntiPiracyDetector'

const CosmicMediaPlayer: React.FC<MediaPlayerProps> = ({ 
  file, 
  config = {},
  className = '',
  onPlayerReady,
  onPlayStateChange,
  onTimeUpdate,
  onMetadataLoad,
  onError,
  cosmkId,
  drmEnabled = false,
  userInfo,
  sessionInfo
}) => {
  const { state, playerRef, videoRef, waveformRef, audioRef } = useMediaPlayer({
    file,
    config,
    onPlayStateChange,
    onTimeUpdate,
    onMetadataLoad,
    onError
  })

  const {
    drmState,
    licenseInfo,
    initializeDRM,
    handleDRMError
  } = useDRMSession({
    cosmkId,
    userInfo,
    sessionInfo,
    enabled: drmEnabled
  })

  const mediaType = getMediaType(file)
  const isVideo = mediaType === 'video'
  const showWaveform = config.showWaveform !== false
  const [antiPiracyActive, setAntiPiracyActive] = React.useState(drmEnabled)

  // Initialize DRM if enabled
  React.useEffect(() => {
    if (drmEnabled && cosmkId && userInfo) {
      initializeDRM()
    }
  }, [drmEnabled, cosmkId, userInfo, initializeDRM])

  // Expose player ref to parent
  React.useEffect(() => {
    if (onPlayerReady && !state.isLoading) {
      onPlayerReady(playerRef)
    }
  }, [onPlayerReady, playerRef, state.isLoading])

  // Handle anti-piracy detection
  const handleAntiPiracyTrigger = React.useCallback((reason: string) => {
    console.warn(`Anti-piracy triggered: ${reason}`)
    if (playerRef && state.isPlaying) {
      playerRef.pause()
    }
    handleDRMError(`Security violation detected: ${reason}`)
    setAntiPiracyActive(false)
  }, [playerRef, state.isPlaying, handleDRMError])

  // If DRM is enabled, use DRM player instead
  if (drmEnabled && cosmkId) {
    return (
      <>
        <DRMPlayer 
          file={file}
          cosmkId={cosmkId}
          config={config}
          className={className}
          userInfo={userInfo}
          sessionInfo={sessionInfo}
          onPlayerReady={onPlayerReady}
          onPlayStateChange={onPlayStateChange}
          onTimeUpdate={onTimeUpdate}
          onMetadataLoad={onMetadataLoad}
          onError={onError}
          drmState={drmState}
          licenseInfo={licenseInfo}
        />
        {antiPiracyActive && (
          <AntiPiracyDetector
            onDetection={handleAntiPiracyTrigger}
            enabled={drmState.status === 'ready'}
          />
        )}
      </>
    )
  }

  const togglePlay = () => {
    if (state.isPlaying) {
      playerRef.pause()
    } else {
      playerRef.play()
    }
  }

  const toggleMute = () => {
    if (state.isMuted) {
      playerRef.setVolume(state.volume)
    } else {
      playerRef.setVolume(0)
    }
  }

  const handleVolumeChange = (newVolume: number) => {
    playerRef.setVolume(newVolume)
  }

  const handleSkipBack = () => {
    const newTime = Math.max(0, state.currentTime - 10)
    playerRef.seek(newTime)
  }

  const handleSkipForward = () => {
    const newTime = Math.min(state.duration, state.currentTime + 10)
    playerRef.seek(newTime)
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (state.duration === 0) return
    
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = percent * state.duration
    playerRef.seek(newTime)
  }

  const getMediaIcon = () => {
    switch (mediaType) {
      case 'video':
        return Video
      case 'audio':
        return Music
      default:
        return Music
    }
  }

  const MediaIcon = getMediaIcon()

  return (
    <motion.div 
      className={`bg-cosmic-dark/80 backdrop-blur-md border-2 border-stellar-cyan/30 rounded-lg p-6 ${className}`}
      variants={gameUIVariants}
      initial="elementEnter"
      animate="elementVisible"
    >
      {/* Video Container - Only shown for video files */}
      {isVideo && (
        <div className="relative mb-6">
          <div className="w-full bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20 overflow-hidden">
            <video 
              ref={videoRef}
              className="w-full h-auto max-h-96 object-contain"
              poster=""
              playsInline
              controls={config.showVideoControls !== false}
            />
          </div>
        </div>
      )}

      {/* Audio Element - Hidden but needed for audio playback */}
      {!isVideo && (
        <audio 
          ref={audioRef}
          className="hidden"
          controls={false}
        />
      )}

      {/* Waveform Container */}
      {showWaveform && (
        <div className="grid grid-cols-1 gap-4 mb-6">
          <div className="col-span-1">
            <div className="w-full h-20 lg:h-24 bg-cosmic-dark/50 rounded-lg border border-stellar-cyan/20 overflow-hidden p-2">
              <div 
                ref={waveformRef} 
                className="w-full h-full"
                data-testid="waveform-container"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Loading overlay */}
      {state.isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-cosmic-dark/80 rounded-lg z-10">
          <div className="text-center">
            <motion.div 
              className="animate-spin rounded-full h-8 w-8 border-b-2 border-stellar-blue mx-auto mb-2"
              variants={gameUIVariants}
              animate="loadingRotate"
            />
            <div className="text-stellar-cyan text-sm">Loading {mediaType}...</div>
          </div>
        </div>
      )}
      
      {/* Responsive Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        {/* Album Art / Video Thumbnail - Left Column */}
        <div className="lg:col-span-2 flex justify-center lg:justify-start">
          {state.metadata?.picture ? (
            <div className="w-20 h-20 lg:w-16 lg:h-16 rounded-lg overflow-hidden border-2 border-stellar-cyan/30">
              <img 
                src={state.metadata.picture}
                alt="Album Art"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 lg:w-16 lg:h-16 rounded-lg bg-gradient-to-br from-stellar-blue to-stellar-cyan flex items-center justify-center border-2 border-stellar-cyan/30">
              <MediaIcon className="h-8 w-8 text-cosmic-black" />
            </div>
          )}
        </div>
        
        {/* Track Info - Middle Column */}
        <div className="lg:col-span-7 text-center lg:text-left">
          <h3 className="text-lg lg:text-xl font-display font-semibold text-white truncate">
            {state.metadata?.title || file.name}
          </h3>
          <p className="text-sm text-stellar-cyan font-mono mt-1">
            {state.metadata?.artist && `${state.metadata.artist}`}
            {state.metadata?.artist && state.metadata?.album && ` • `}
            {state.metadata?.album && `${state.metadata.album}`}
          </p>
          <p className="text-xs text-stellar-cyan/60 font-mono mt-1">
            {state.metadata?.year && `${state.metadata.year} • `}
            {state.metadata?.genre && state.metadata.genre}
            {isVideo && state.metadata?.resolution && 
              ` • ${state.metadata.resolution.width}x${state.metadata.resolution.height}`
            }
          </p>
        </div>
        
        {/* Time Info - Right Column */}
        <div className="lg:col-span-3 text-center lg:text-right">
          <p className="text-lg font-mono text-white font-semibold">
            {formatDuration(state.currentTime)}
          </p>
          <p className="text-sm text-stellar-cyan/60 font-mono">
            / {formatDuration(state.duration)}
          </p>
          <p className="text-xs text-stellar-cyan/40 font-mono mt-1">
            {file.type.split('/')[1]?.toUpperCase() || mediaType.toUpperCase()}
            {drmEnabled && (
              <span className="ml-2 inline-flex items-center text-stellar-cyan">
                <Shield className="h-3 w-3 mr-1" />
                DRM
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Responsive Controls Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
        {/* Playback Controls - Left */}
        <div className="flex items-center justify-center lg:justify-start space-x-4">
          {/* Skip Back */}
          <motion.button
            onClick={handleSkipBack}
            disabled={state.isLoading}
            className="w-8 h-8 text-stellar-cyan hover:text-white transition-colors disabled:opacity-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Skip back 10s"
          >
            <SkipBack className="h-5 w-5" />
          </motion.button>

          {/* Play/Pause */}
          <motion.button
            onClick={togglePlay}
            disabled={state.isLoading}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              state.isLoading 
                ? 'bg-stellar-gray/50 text-stellar-gray cursor-not-allowed' 
                : 'bg-stellar-blue text-cosmic-black hover:bg-stellar-cyan'
            }`}
            whileHover={!state.isLoading ? { scale: 1.1 } : {}}
            whileTap={!state.isLoading ? { scale: 0.9 } : {}}
          >
            {state.isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </motion.button>

          {/* Skip Forward */}
          <motion.button
            onClick={handleSkipForward}
            disabled={state.isLoading}
            className="w-8 h-8 text-stellar-cyan hover:text-white transition-colors disabled:opacity-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Skip forward 10s"
          >
            <SkipForward className="h-5 w-5" />
          </motion.button>

          {/* Stop/Reset */}
          <motion.button
            onClick={() => playerRef.stop()}
            disabled={state.isLoading}
            className="w-8 h-8 text-stellar-cyan hover:text-white transition-colors disabled:opacity-50"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Stop and reset"
          >
            <RotateCcw className="h-5 w-5" />
          </motion.button>
        </div>

        {/* Progress Bar - Center (Hidden on mobile, shown on larger screens) */}
        <div className="hidden lg:block">
          <div 
            className="w-full bg-stellar-cyan/20 rounded-full h-2 cursor-pointer"
            onClick={handleSeek}
          >
            <motion.div
              className="bg-gradient-to-r from-stellar-blue to-stellar-cyan h-2 rounded-full"
              style={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
        </div>

        {/* Volume Control - Right */}
        <div className="flex items-center justify-center lg:justify-end space-x-3">
          <motion.button
            onClick={toggleMute}
            className="text-stellar-cyan hover:text-white transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {state.isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
          </motion.button>
          
          <div className="flex items-center space-x-2">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={state.isMuted ? 0 : state.volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="w-16 lg:w-20 h-2 bg-stellar-cyan/30 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
        </div>
      </div>

      {/* Mobile Progress Bar */}
      <div className="lg:hidden mt-4">
        <div 
          className="w-full bg-stellar-cyan/20 rounded-full h-2 cursor-pointer"
          onClick={handleSeek}
        >
          <motion.div
            className="bg-gradient-to-r from-stellar-blue to-stellar-cyan h-2 rounded-full"
            style={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${state.duration > 0 ? (state.currentTime / state.duration) * 100 : 0}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
      </div>

      {/* Error state */}
      {state.error && (
        <motion.div 
          className="mt-4 p-3 bg-danger-red/20 border border-danger-red rounded-lg"
          variants={gameUIVariants}
          initial="elementEnter"
          animate="elementVisible"
        >
          <p className="text-danger-red text-sm font-mono">
            <strong>{state.error.code}:</strong> {state.error.message}
          </p>
        </motion.div>
      )}

      {/* Additional Info for Video */}
      {isVideo && state.metadata?.resolution && (
        <div className="mt-4 text-xs text-stellar-cyan/60 font-mono text-center">
          Resolution: {state.metadata.resolution.width}×{state.metadata.resolution.height}
          {state.metadata.bitrate && ` • Bitrate: ${Math.round(state.metadata.bitrate / 1000)}kbps`}
          {state.metadata.codec && ` • Codec: ${state.metadata.codec}`}
        </div>
      )}
    </motion.div>
  )
}

export default CosmicMediaPlayer